import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useNoteCreation } from "@/components/note-creation-provider";
import { checkUploadFile, MAX_UPLOAD_BYTES } from "@/lib/upload/limits";
import { canCompress, compressImageFile } from "@/lib/upload/compress-image";

type Step =
  | { name: "select" }
  | { name: "compressing" }
  | { name: "preview"; file: File; previewUrl: string };

/**
 * "새 설교 노트" 버튼 → 모달. 파일 선택만으로는 아무것도 안 보내고,
 * "확인"을 눌러야 업로드가 시작된다. 근데 실제 업로드+분석은 여기서
 * 안 하고 NoteCreationProvider(레이아웃 레벨)에 맡긴다 — "확인" 누르면
 * 모달은 바로 닫히고, 진행 상황은 화면 우측 하단 토스트로 보여준다.
 * 결과 화면은 노트 상세 페이지(/notes/[id])가 담당한다.
 */
export function UploadNoteDialog({
  children,
  className,
  size,
  disabled,
}: {
  children?: React.ReactNode;
  className?: string;
  size?: "sm" | "default";
  /** 무료 플랜 월 상한을 다 썼을 때 부모(note-library.tsx)가 넘겨준다. */
  disabled?: boolean;
}) {
  const { state, startUpload } = useNoteCreation();
  // 이미 업로드 처리 중(다른 노트가 분석되는 중)일 때 또 시작하면 두 요청이 동시에
  // 날아가서 토스트 상태가 뒤섞이고, 월 상한 검사도 경쟁 상태(TOCTOU)가 생긴다
  // (DB 트리거로도 막아뒀지만, 애초에 버튼에서부터 막는 게 사용자 경험상 낫다).
  const isBusy = state.status === "creating";
  const isDisabled = disabled || isBusy;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>({ name: "select" });
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError(null);

    // 형식은 압축 전에 먼저 걸러낸다(HEIC 등 압축 대상이 아닌 파일도 이 검사는 통과해야 한다).
    const typeCheck = checkUploadFile(file);
    if (!typeCheck.ok && typeCheck.status === 415) {
      setFileError(typeCheck.message);
      e.target.value = "";
      return;
    }

    let uploadFile = file;
    if (file.size > MAX_UPLOAD_BYTES && canCompress(file)) {
      setStep({ name: "compressing" });
      uploadFile = await compressImageFile(file, MAX_UPLOAD_BYTES);
    }

    // 압축해도 안 줄거나(HEIC 등) 여전히 크면 그때 막는다.
    const sizeCheck = checkUploadFile(uploadFile);
    if (!sizeCheck.ok) {
      setFileError(sizeCheck.message);
      setStep({ name: "select" });
      e.target.value = "";
      return;
    }

    setStep({ name: "preview", file: uploadFile, previewUrl: URL.createObjectURL(uploadFile) });
  }

  function handleConfirm() {
    if (step.name !== "preview") return;
    startUpload(step.file);
    setOpen(false);
    setStep({ name: "select" });
  }

  return (
    <Dialog
      open={open && !isDisabled}
      onOpenChange={(next) => {
        if (next && isDisabled) return; // 처리 중이거나 월 상한에 도달했으면 아예 안 연다.
        setOpen(next);
        if (!next) {
          setStep({ name: "select" });
          setFileError(null);
        }
      }}
    >
      <DialogTrigger
        render={<Button className={className} size={size} disabled={isDisabled} />}
        title={
          disabled
            ? "이번 달 업로드 한도에 도달했어요. 다음 달에 다시 시도해주세요."
            : isBusy
              ? "다른 노트를 처리하는 중이에요. 끝나면 다시 시도해주세요."
              : undefined
        }
      >
        {isBusy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
        {children ?? "새 설교 노트"}
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>새 설교 노트</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {step.name === "preview" ? (
            <img
              src={step.previewUrl}
              alt="업로드한 노트 미리보기"
              className="aspect-[3/4] w-full rounded-md border object-cover"
            />
          ) : step.name === "compressing" ? (
            <div className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              사진 최적화 중...
            </div>
          ) : (
            <label
              htmlFor="upload-note-image"
              className="flex aspect-[3/4] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:bg-accent"
            >
              노트 사진 선택
            </label>
          )}

          {fileError ? (
            <p className="text-sm text-destructive" role="alert">
              {fileError}
            </p>
          ) : null}

          {step.name === "select" ? (
            <input
              id="upload-note-image"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="text-xs"
            />
          ) : step.name === "preview" ? (
            <Button onClick={handleConfirm}>확인</Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
